import classNames from 'classnames';
import { CurrencyLabel } from 'components/shared/currency-pair-switcher/currency-label';
import { ValidationFeedbackMessage } from 'components/shared/FormGroup/ErrorMessages';
import { IntlKey } from 'locales';
import { FC, PropsWithChildren } from 'react';
import { FormGroup } from 'react-bootstrap';
import { FormattedMessage } from 'react-intl';

import {
  CurrencyPairSwitcherContext,
  type CurrencyPairSwitcherContextProps,
  useCurrencyPairSwitcherContext,
} from './use-currency-pair-switcher-context';

type CurrencyPairSwitcherLabelProps = {
  isReadOnly?: boolean;
};

type CurrencyPairSwitcherProps = PropsWithChildren<CurrencyPairSwitcherContextProps> & {
  className?: string;
};

export const CurrencyPairSwitcher: FC<CurrencyPairSwitcherProps> & {
  Label: FC<CurrencyPairSwitcherLabelProps>;
  Input: FC;
  Validation: FC;
} = ({ children, className, ...props }) => {
  return (
    <CurrencyPairSwitcherContext.Provider value={props}>
      <FormGroup className={classNames(className, { 'my-0': props.isLabelHidden })} data-e2e={props.id} id={props.id}>
        {children}
      </FormGroup>
    </CurrencyPairSwitcherContext.Provider>
  );
};

CurrencyPairSwitcher.Label = function Label({ isReadOnly }) {
  const { id, switchedCurrencyTranslationKey } = useCurrencyPairSwitcherContext();
  return (
    <label htmlFor={id} className="col-form-label d-flex justify-content-between">
      <FormattedMessage id={`form.${switchedCurrencyTranslationKey}` as IntlKey} tagName="div" />
      {!isReadOnly && <FormattedMessage id="form.AlternativeCurrency" tagName="div" />}
    </label>
  );
};

CurrencyPairSwitcher.Input = function Input() {
  const { value, currency1, currency2, onToggleCurrency, isLabelHidden, tabIndex } = useCurrencyPairSwitcherContext();
  const primaryCurrency = value ?? currency1;
  const alternateCurrency = value === currency1 ? currency2 : currency1;
  const handleOnClick = () => {
    onToggleCurrency(alternateCurrency);
  };

  return (
    <div className={classNames('currency-pair-switcher d-flex justify-content-between', { 'mb-n3': isLabelHidden })}>
      <CurrencyLabel
        className={classNames('btn-outline-primary flex-grow-1', { 'px-2': isLabelHidden })}
        currency={primaryCurrency}
        onClick={handleOnClick}
      />
      <div className="flex-grow-1 d-flex justify-content-center">
        <button type="button" className="btn btn-discreet-secondary" onClick={handleOnClick} tabIndex={tabIndex}>
          <em className="icon">swap_horiz</em>
        </button>
      </div>
      <CurrencyLabel
        className={classNames('btn-outline-secondary flex-grow-1', { 'px-2': isLabelHidden })}
        currency={alternateCurrency}
        onClick={handleOnClick}
      />
    </div>
  );
};

CurrencyPairSwitcher.Validation = function Validation() {
  const { validationError } = useCurrencyPairSwitcherContext();
  return <ValidationFeedbackMessage validationError={validationError} />;
};
